import { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { setDoc, doc } from "firebase/firestore";
import { sendEmail } from "../components/sendemail";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";

export default function RegisterTutor() {
  const [form, setForm] = useState({
    name: "",
    bio: "",
    schoolYear: "",
    subjects: [],
    gradeLevels: [],
    maxStudents: 1,
    availability: {},
    email: "",
    password: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const toggleAvailability = (day, time) => {
    setForm(prev => {
      const dayTimes = prev.availability[day] || [];
      const updatedTimes = dayTimes.includes(time)
        ? dayTimes.filter(t => t !== time)
        : [...dayTimes, time];
      return {
        ...prev,
        availability: { ...prev.availability, [day]: updatedTimes }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const userCred = await createUserWithEmailAndPassword(auth, form.email, form.password);

      await setDoc(doc(db, "users", userCred.user.uid), {
        name: form.name,
        bio: form.bio,
        schoolYear: form.schoolYear,
        subjects: form.subjects,
        gradeLevels: form.gradeLevels,
        availability: form.availability,
        email: form.email,
        role: "tutor",
        uid: userCred.user.uid,
        atCapacity: false,
        maxStudents: form.maxStudents,
        currentStudents: 0,
        totalHours: 0,
        // ✅ NEW: tutors start as "pending" until admin approves
        status: "pending"
      });

      // The account + profile are the important part of registration.
      // A broken/misconfigured mail step (e.g. SMTP auth issues) shouldn't
      // make the whole registration look like it failed, so this is
      // isolated in its own try/catch and only logged, not surfaced to the user.
      try {
        await sendEmail(
          form.email,
          "Thank you for applying to SOWS!",
          `<p>Hello ${form.name}!</p>
          <p>Thank you for your interest in joining the SOWS Fund tutoring program as a Tutor. Please allow us a few days to review your application, and we will follow up soon with the next few steps. We look forward to potentially welcoming you to the SOWS team!</p>
          <p>We love people of action like you. If you have ideas or would like to contribute in any other way please do not hesitate to reach out at <a href="mailto:sowstutoring@leonardeducation.org">sowstutoring@leonardeducation.org</a>!</p>
          <p>Many thanks,<br/>SOWS</p>`
        );
      } catch (emailErr) {
        console.error("Tutor account was created, but the welcome email failed to send:", emailErr);
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Tutor registration failed:", err);
      setError(
        err?.code === "auth/email-already-in-use"
          ? "That email is already registered. Try logging in instead, or use a different email."
          : err?.code === "auth/weak-password"
          ? "Password should be at least 6 characters."
          : err?.code === "auth/invalid-email"
          ? "Please enter a valid email address."
          : err?.message || "Something went wrong submitting your application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Pending approval screen ─────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="form-container" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "60px", marginBottom: "20px" }}>🌱</div>
        <h1>Application Submitted!</h1>
        <p style={{ fontSize: "18px", maxWidth: "500px", margin: "0 auto 30px" }}>
          Thank you for applying to tutor with SOWS! Your application is under review.
          You'll receive an email once you've been approved and can begin tutoring.
        </p>
        <p style={{ color: "gray" }}>
          Questions? Reach out at{" "}
          <a href="mailto:Sowstutoring@leonardeducation.org">Sowstutoring@leonardeducation.org</a>
        </p>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h1>Register as a Tutor</h1>

      <form onSubmit={handleSubmit}>

        <div className="form-group">
          <label>Name</label>
          <input onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <p style={{ fontSize: "17px", color: "gray", marginTop: "5px" }}>
          Name will be displayed on your profile and visible to students. Feel free to only use your first name or initials if you prefer!
        </p>

        <div className="form-group">
          <label>Bio</label>
          <textarea onChange={e => setForm({ ...form, bio: e.target.value })} />
        </div>

        <div className="form-group">
          <label>School Year</label>
          <input onChange={e => setForm({ ...form, schoolYear: e.target.value })} />
        </div>

        <div className="form-group">
          <label>Subjects You Can Tutor</label>
          <div className="subject-checkboxes">
            {["English", "Math", "Biology", "Chemistry", "Physics", "Standardized Tests", "Applications"].map(subject => (
              <label key={subject}>
                <input
                  type="checkbox"
                  checked={form.subjects.includes(subject)}
                  onChange={() => {
                    if (form.subjects.includes(subject)) {
                      setForm({ ...form, subjects: form.subjects.filter(s => s !== subject) });
                    } else {
                      setForm({ ...form, subjects: [...form.subjects, subject] });
                    }
                  }}
                />
                {" "}{subject}
              </label>
            ))}
          </div>
        </div>
<div className="form-group">
  <label>Grade Levels You're Comfortable Tutoring</label>
  <div className="subject-checkboxes">
    {["Elementary School", "Middle School", "High School", "College"].map(level => (
      <label key={level}>
        <input
          type="checkbox"
          checked={(form.gradeLevels || []).includes(level)}
          onChange={() => {
            const current = form.gradeLevels || [];
            if (current.includes(level)) {
              setForm({ ...form, gradeLevels: current.filter(g => g !== level) });
            } else {
              setForm({ ...form, gradeLevels: [...current, level] });
            }
          }}
        />
        {" "}{level}
      </label>
    ))}
  </div>
</div>
        <div className="form-group">
          <label>How many students can you tutor at one time?</label>
          <input
            type="number"
            min="1"
            onChange={e => setForm({ ...form, maxStudents: Number(e.target.value) })}
          />
        </div>

<div className="form-group">
  <label>Availability</label>
  <div style={{ overflowX: "auto" }}>
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "8px",
      minWidth: "500px"
    }}>
      {/* DAY HEADERS */}
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => {
        const fullDay = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][i];
        const hasAny = form.availability[fullDay]?.length > 0;
        return (
          <div key={day} style={{
            textAlign: "center",
            padding: "8px 4px",
            borderRadius: "12px",
            fontWeight: "bold",
            fontSize: "14px",
            background: hasAny ? "#3d4f0c" : "#d4cebc",
            color: hasAny ? "#f6f0dc" : "#555"
          }}>
            {day}
          </div>
        );
      })}

      {/* TIME CHIPS PER DAY */}
      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => (
        <div key={day} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {["Morning", "Afternoon", "Evening"].map(time => {
            const selected = form.availability[day]?.includes(time);
            return (
              <span
                key={time}
                onClick={() => toggleAvailability(day, time)}
                style={{
                  padding: "6px 4px",
                  borderRadius: "12px",
                  fontWeight: "600",
                  fontSize: "12px",
                  cursor: "pointer",
                  textAlign: "center",
                  background: selected ? "#e57386" : "#f6f0dc",
                  color: selected ? "black" : "#555",
                  border: selected ? "2px solid #e57386" : "2px solid #d4cebc",
                  transition: "all 0.2s",
                  userSelect: "none"
                }}
              >
                {selected ? "✓" : ""} {time}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  </div>
</div>

        <div className="form-group">
          <label>Email</label>
          <input type="email" onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input type="password" onChange={e => setForm({ ...form, password: e.target.value })} />
        </div>

        {error && (
          <p style={{ color: "#c0392b", fontWeight: "600", marginTop: "10px" }}>
            {error}
          </p>
        )}

        <button className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Create Tutor Account"}
        </button>

      </form>
    </div>
  );
}